import React, { Dispatch } from 'react';
import cn from 'classnames';
import styles from './IconAndColorPicker.module.scss';
import PopoverMenu from '@components/Menus/PopoverMenu';
import { DefaultNoteIcon, noteIconTypeIconMap } from '@components/Icons/NoteType';
import { Color } from '@views/Group/Form/Colors';
import { NoteIconType, NoteType } from '@models/Note';
import { hexToRgb } from '@support/number';

const orderedColors = [
  [null, 'auto'],
  ['#57AEB2', 'turquoise'],
  ['#0072AF', 'ocean blue'],
  ['#AE98C1', 'lilac'],
  ['#349D63', 'green'],
  ['#FFB74A', 'yellow'],
  ['#EB515B', 'cherry'],
  ['#DF5D86', 'pink'],
];

const orderedNoteIconTypes: NoteIconType[] = [
  null,
  'smile',
  'star',
  'smartphone',
  'desktopcomputer',
  'car',
  'airplane',
  'heart',
  'video',
  'cake',
  'binoculars',
  'health',
  'eat',
  'bookmark',
  'call',
  'map',
  'mic',
  'music',
  'water',
  'sun',
  'moon',
  'drive',
  'piechart',
  'game',
  'inbox',
  'personframe',
  'hourglass',
  'navigation',
  'building',
  'message',
  'flag',
  'photo',
  'briefcase',
  'dollarsign',
  'rolodex',
  'leaf',
  'cloud',
  'paw',
  'flower',
  'watch',
  'cube',
  'hand',
  'cup',
  'shield',
  'key',
  'paintbrush',
  'lightbulb',
  'book',
];

const NoteIconSelectorMenu = ({
  selectedColor,
  setSelectedColor,
  selectedIconType,
  setSelectedIconType,
}: {
  selectedColor: string | null;
  setSelectedColor: Dispatch<string | null>;
  selectedIconType: NoteIconType | null;
  setSelectedIconType: Dispatch<NoteIconType | null>;
}) => (
  <>
    <div className={cn(styles.menuGrid, styles.colors)} key="color">
      {orderedColors.map(([color, label]) => (
        <Color
          key={label}
          hex={color}
          label={label}
          active={color === selectedColor}
          onMouseDown={() => setSelectedColor(color)}
          placement="right"
        />
      ))}
    </div>
    <div className={cn(styles.menuGrid, styles.icons)} key="icon">
      {orderedNoteIconTypes.map((iconType) => {
        const Icon = noteIconTypeIconMap[iconType] || DefaultNoteIcon;

        return (
          <div
            key={String(iconType)}
            className={cn(styles.icon, selectedIconType == iconType && styles.selected)}
            onMouseDown={() => setSelectedIconType(iconType)}
          >
            <Icon color={selectedColor} />
          </div>
        );
      })}
    </div>
  </>
);

export const NoteIcon = ({
  selectedColor,
  selectedIconType,
  editable = true,
  className,
}: {
  selectedColor: string | null;
  selectedIconType: NoteIconType | null;
  editable?: boolean;
  className?: string;
}) => {
  const Icon = noteIconTypeIconMap[selectedIconType] || DefaultNoteIcon;
  let width = 14;
  let height = 14;
  let backgroundImage = 'none';

  if (!selectedIconType) {
    width = 13;
    height = 13;

    if (selectedColor) {
      const rgb = hexToRgb(selectedColor);
      backgroundImage = rgb ? `linear-gradient(rgba(${rgb}, 0.2), rgba(${rgb}, 0.2))` : 'none';
    }
  }

  return (
    <div
      className={cn(
        styles.noteIcon,
        !selectedIconType && styles.boxed,
        editable && styles.editable,
        className,
      )}
      style={{ backgroundImage }}
    >
      <Icon color={selectedColor} width={width} height={height} />
    </div>
  );
};

export default function NoteIconAndColorPicker({
  noteType,
  updateNoteType,
  setSelectingIconAndColor,
}: {
  noteType: NoteType;
  updateNoteType: (noteType: NoteType) => void;
  setSelectingIconAndColor?: Dispatch<boolean>;
}) {
  const { color, icon } = noteType;

  return (
    <PopoverMenu
      trigger={<NoteIcon selectedColor={color} selectedIconType={icon} />}
      menu={
        <NoteIconSelectorMenu
          selectedColor={color}
          setSelectedColor={(color) => updateNoteType({ ...noteType, color })}
          selectedIconType={icon}
          setSelectedIconType={(icon) => updateNoteType({ ...noteType, icon })}
        />
      }
      onOpen={() => setSelectingIconAndColor?.(true)}
      onClose={() => setSelectingIconAndColor?.(false)}
    />
  );
}
